import { Lock, LogIn, Mail } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { AuthHero } from "../components/AuthHero";
import { PasswordInput } from "../components/PasswordInput";
import { ApiError, login } from "../lib/api";
import "../styles/auth.css";

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await login({ email, password });
      navigate("/trees", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.detail === "email_not_verified") {
        setError(t("auth.emailNotVerified"));
      } else if (err instanceof ApiError) {
        setError(t("auth.loginError"));
      } else {
        setError(t("common.error"));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page auth-page--centered">
      <AuthHero homeLink />
      <div className="auth-content">
        <div className="auth-card">
          <div className="auth-card__header">
            <div className="auth-card__icon">
              <LogIn size={24} aria-hidden="true" />
            </div>
            <h2>{t("auth.login")}</h2>
            <p className="auth-prompt">{t("auth.loginPrompt")}</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="auth-field">
              <label htmlFor="email">
                <Mail size={13} className="auth-label-icon" aria-hidden="true" />
                {t("auth.email")}
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-1p-allow
              />
            </div>

            <div className="auth-field">
              <label htmlFor="password">
                <Lock size={13} className="auth-label-icon" aria-hidden="true" />
                {t("auth.password")}
              </label>
              <PasswordInput
                id="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-1p-allow
              />
            </div>

            <p className="auth-footer" style={{ marginTop: 0, textAlign: "right" }}>
              <Link to="/forgot-password">{t("auth.forgotPassword")}</Link>
            </p>

            {error && (
              <p className="auth-error" role="alert">
                {error}
              </p>
            )}

            <button className="auth-submit" type="submit" disabled={loading}>
              {loading ? t("common.loading") : t("auth.login")}
            </button>
          </form>

          <p className="auth-footer">
            {t("auth.noAccount")} <Link to="/register">{t("auth.register")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
